import * as React from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { 
    ArrowUpIcon, 
    ShieldCheckIcon, 
    DatabaseIcon, 
    KeyIcon, 
    UserCheckIcon,
    BellIcon,
    MailIcon,
    EyeIcon,
    LockIcon
} from 'lucide-react'
import { Link, useNavigate } from "react-router-dom"
import { useTenant } from "@/context/TenantContext"
import { useTheme } from "@/context/ThemeContext"

export default function PrivacyPolicy() {
    const [showScrollTop, setShowScrollTop] = React.useState(false)
    const [activeSection, setActiveSection] = React.useState("")
    const { branding } = useTenant()
    const { theme } = useTheme()
    const navigate = useNavigate()

    // Determine if we should use dark logo
    const isDarkMode = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    const effectiveLogoUrl = isDarkMode && branding?.dark_logo_url ? branding.dark_logo_url : branding?.logo_url;

    const renderLogo = () => {
        if (!effectiveLogoUrl) {
            return (
                <span className="text-2xl font-semibold text-primary">
                    {branding?.company_name || 'VoiceBot'}
                </span>
            );
        }

        return (
            <img 
                src={effectiveLogoUrl}
                alt={branding?.company_name || 'Company Logo'}
                className="max-h-10 w-auto max-w-[160px] object-contain cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => navigate('/')}
                onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    const parent = target.parentElement;
                    if (parent) {
                        parent.innerHTML = `<span class="text-2xl font-semibold text-primary cursor-pointer">${branding?.company_name || 'VoiceBot'}</span>`;
                        parent.onclick = () => navigate('/');
                    }
                }}
            />
        );
    };

    React.useEffect(() => {
        const handleScroll = () => {
            setShowScrollTop(window.pageYOffset > 300)

            const sections = document.querySelectorAll("section[id]")
            let currentActiveSection = ""

            sections.forEach((section) => {
                const sectionTop = (section as HTMLElement).offsetTop
                const sectionHeight = (section as HTMLElement).clientHeight
                if (window.pageYOffset >= sectionTop - 100 && window.pageYOffset < sectionTop + sectionHeight - 100) {
                    currentActiveSection = section.id
                }
            })

            if (window.innerHeight + window.pageYOffset >= document.body.offsetHeight - 100) {
                currentActiveSection = sections[sections.length - 1].id
            }

            setActiveSection(currentActiveSection)
        }

        window.addEventListener('scroll', handleScroll)
        handleScroll() // Initial check
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const scrollToSection = (sectionId: string) => {
        const section = document.getElementById(sectionId)
        if (section) {
            const offset = 80
            const elementPosition = section.getBoundingClientRect().top
            const offsetPosition = elementPosition + window.pageYOffset - offset

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            })
        }
    }

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                duration: 0.2,
                ease: "easeOut",
                staggerChildren: 0.03
            }
        }
    }

    const itemVariants = {
        hidden: { opacity: 0, y: 8 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.25,
                ease: [0.22, 1, 0.36, 1] // Professional easing curve
            }
        }
    }

    const sections = [
        { id: "introduction", title: "Introduction", icon: ShieldCheckIcon },
        { id: "data-collection", title: "Data Collection", icon: DatabaseIcon },
        { id: "data-usage", title: "Data Usage", icon: EyeIcon },
        { id: "data-protection", title: "Data Protection", icon: LockIcon },
        { id: "data-sharing", title: "Data Sharing", icon: KeyIcon },
        { id: "user-rights", title: "Your Rights", icon: UserCheckIcon },
        { id: "updates", title: "Policy Updates", icon: BellIcon },
        { id: "contact", title: "Contact Us", icon: MailIcon },
    ]

    const lastUpdated = "October 20, 2025"

    return (
        <div className="min-h-screen bg-muted/30 relative">
            {/* Animated gradient background */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div 
                    className="absolute -top-[40%] -right-[20%] w-[60%] h-[60%] rounded-full bg-primary/[0.04] dark:bg-primary/[0.06] blur-3xl animate-pulse"
                    style={{ animationDuration: '8s' }}
                />
                <div 
                    className="absolute -bottom-[40%] -left-[20%] w-[60%] h-[60%] rounded-full bg-primary/[0.05] dark:bg-primary/[0.08] blur-3xl animate-pulse"
                    style={{ animationDuration: '8s', animationDelay: '4s' }}
                />
            </div>

            {/* Header */}
            <motion.header 
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60"
            >
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
                    <div className="flex h-16 items-center justify-between">
                        <div className="flex items-center gap-8">
                            {renderLogo()}
                            <span className="hidden sm:inline text-sm text-muted-foreground">
                                Privacy Policy
                            </span>
                        </div>
                        <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => navigate(-1)}
                            className="text-sm"
                        >
                            Back
                        </Button>
                    </div>
                </div>
            </motion.header>

            {/* Main Content */}
            <div className="relative">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-8 lg:py-12">
            <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                        {/* Hero Section */}
                        <motion.div variants={itemVariants} className="mb-12">
                            <div className="max-w-3xl">
                                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
                                    <ShieldCheckIcon className="h-3.5 w-3.5" />
                                    Privacy & Data Protection
                                </div>
                                <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">
                                    Privacy Policy
                                </h1>
                                <p className="text-lg text-muted-foreground mb-4">
                                    Your privacy is our priority. This policy explains how we collect, use, protect, and handle your personal information when you use our services.
                                </p>
                                <p className="text-sm text-muted-foreground/70">
                                    Last updated: {lastUpdated}
                                </p>
                            </div>
                </motion.div>

                        {/* Content Grid */}
                        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                            {/* Sidebar Navigation - Hidden on mobile */}
                            <motion.aside 
                                variants={itemVariants}
                                className="hidden lg:block lg:col-span-1"
                            >
                                <div className="sticky top-24">
                                    <Card className="shadow-sm">
                                        <CardContent className="p-4">
                                            <h3 className="text-sm font-semibold mb-3">On this page</h3>
                                            <nav>
                                                <ul className="space-y-1">
                                                    {sections.map((section) => {
                                                        const Icon = section.icon
                                                        return (
                                                            <li key={section.id}>
                                                                <button
                                                                    onClick={() => scrollToSection(section.id)}
                                                                    className={`
                                                                        w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs
                                                                        transition-colors text-left
                                                                        ${activeSection === section.id 
                                                                            ? 'bg-primary/10 text-primary font-medium' 
                                                                            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                                                                        }
                                                                    `}
                                                                >
                                                                    <Icon className="h-3.5 w-3.5 shrink-0" />
                                                                    <span className="truncate">{section.title}</span>
                                                                </button>
                                                            </li>
                                                        )
                                                    })}
                                                </ul>
                                            </nav>
                                        </CardContent>
                                    </Card>
                                </div>
                            </motion.aside>

                            {/* Main Content */}
                            <motion.main 
                                variants={itemVariants}
                                className="lg:col-span-3"
                            >
                                <Card className="shadow-sm">
                                    <CardContent className="p-6 sm:p-8 space-y-10">
                                        <section id="introduction">
                                            <h2 className="scroll-m-20 text-2xl font-semibold tracking-tight mb-4">1. Introduction</h2>
                                            <div className="text-muted-foreground space-y-4 [&>p]:leading-7 [&>ul]:my-4 [&>ul]:ml-6 [&>ul>li]:mt-2">
                                                <p>
                                                    At {branding?.company_name || 'VoiceBot'}, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy outlines our practices concerning the collection, use, storage, and protection of your data when you use our AI-powered voice bot services.
                                                </p>
                                                <p>
                                                    This policy applies to all information collected through our services, including our website, platform, and any related services, sales, marketing, or events (collectively referred to as "Services").
                                                </p>
                                                <p>
                                                    By using our Services, you consent to the data practices described in this policy. If you do not agree with our policies and practices, please do not use our Services.
                                                </p>
                                            </div>
                                </section>

                                        <section id="data-collection">
                                            <h2 className="scroll-m-20 text-2xl font-semibold tracking-tight mb-4">2. Information We Collect</h2>
                                            <div className="text-muted-foreground space-y-4 [&>p]:leading-7 [&>ul]:my-4 [&>ul]:ml-6 [&>ul>li]:mt-2">
                                                <p>
                                                    We collect several types of information to provide and improve our Services:
                                                </p>
                                                
                                                <div className="rounded-lg bg-muted p-4 not-prose space-y-4">
                                                    <div>
                                                        <h4 className="text-sm font-semibold text-foreground mb-2">Personal Information</h4>
                                                        <ul className="list-disc pl-5 text-sm text-muted-foreground space-y-1">
                                                            <li>Name and contact information (email address, phone number)</li>
                                                            <li>Company name and business information</li>
                                                            <li>Account credentials (username, password)</li>
                                                            <li>Billing and payment information</li>
                                                        </ul>
                                                    </div>
                                                    
                                                    <div>
                                                        <h4 className="text-sm font-semibold text-foreground mb-2">Usage Information</h4>
                                                        <ul className="list-disc pl-5 text-sm text-muted-foreground space-y-1">
                                                            <li>Call recordings and transcripts</li>
                                                            <li>Voice data and audio files</li>
                                                            <li>Conversation analytics and metrics</li>
                                                            <li>Log data (IP address, browser type, access times)</li>
                                                            <li>Device information and unique identifiers</li>
                                                        </ul>
                                                    </div>

                                                    <div>
                                                        <h4 className="text-sm font-semibold text-foreground mb-2">Automatically Collected Information</h4>
                                                        <ul className="list-disc pl-5 text-sm text-muted-foreground space-y-1">
                                                            <li>Cookies and similar tracking technologies</li>
                                                            <li>Usage patterns and preferences</li>
                                                            <li>Performance and diagnostic data</li>
                                    </ul>
                                                    </div>
                                                </div>

                                                <p>
                                                    You can choose not to provide certain information, but this may limit your ability to use some features of our Services.
                                                </p>
                                            </div>
                                </section>

                                        <section id="data-usage">
                                            <h2 className="scroll-m-20 text-2xl font-semibold tracking-tight mb-4">3. How We Use Your Information</h2>
                                            <div className="text-muted-foreground space-y-4 [&>p]:leading-7 [&>ul]:my-4 [&>ul]:ml-6 [&>ul>li]:mt-2">
                                                <p>
                                                    We use the information we collect for various purposes, including:
                                                </p>
                                                <ul className="list-disc pl-6 space-y-2">
                                                    <li><strong>Service Delivery:</strong> To provide, maintain, and improve our voice bot services</li>
                                                    <li><strong>Personalization:</strong> To customize your experience and provide personalized features</li>
                                                    <li><strong>Communication:</strong> To send you service updates, technical notices, security alerts, and support messages</li>
                                                    <li><strong>Analytics:</strong> To analyze usage patterns and optimize service performance</li>
                                                    <li><strong>AI Training:</strong> To train and improve our AI models and natural language processing capabilities</li>
                                                    <li><strong>Security:</strong> To detect, prevent, and address technical issues, fraud, and security vulnerabilities</li>
                                                    <li><strong>Compliance:</strong> To comply with legal obligations and enforce our terms and policies</li>
                                                    <li><strong>Business Operations:</strong> To process payments, manage accounts, and provide customer support</li>
                                    </ul>
                                                <p>
                                                    We do not sell your personal information to third parties for their marketing purposes.
                                    </p>
                                            </div>
                                </section>

                                        <section id="data-protection">
                                            <h2 className="scroll-m-20 text-2xl font-semibold tracking-tight mb-4">4. Data Protection & Security</h2>
                                            <div className="text-muted-foreground space-y-4 [&>p]:leading-7 [&>ul]:my-4 [&>ul]:ml-6 [&>ul>li]:mt-2">
                                                <p>
                                                    We implement robust security measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction:
                                                </p>
                                                <ul className="list-disc pl-6 space-y-2">
                                                    <li><strong>Encryption:</strong> All data is encrypted in transit using TLS/SSL and at rest using industry-standard encryption algorithms</li>
                                                    <li><strong>Access Controls:</strong> Strict access controls and authentication mechanisms limit who can access your data</li>
                                                    <li><strong>Infrastructure Security:</strong> Secure data centers with 24/7 monitoring, redundancy, and backup systems</li>
                                                    <li><strong>Regular Audits:</strong> Periodic security assessments and penetration testing</li>
                                                    <li><strong>Employee Training:</strong> All staff members are trained on data protection and privacy best practices</li>
                                                    <li><strong>Incident Response:</strong> Comprehensive incident response and breach notification procedures</li>
                                                </ul>
                                                <p>
                                                    While we strive to protect your personal information, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security but are committed to using reasonable measures to protect your data.
                                                </p>
                                            </div>
                                </section>

                                        <section id="data-sharing">
                                            <h2 className="scroll-m-20 text-2xl font-semibold tracking-tight mb-4">5. Data Sharing & Disclosure</h2>
                                            <div className="text-muted-foreground space-y-4 [&>p]:leading-7 [&>ul]:my-4 [&>ul]:ml-6 [&>ul>li]:mt-2">
                                                <p>
                                                    We may share your information in the following circumstances:
                                                </p>
                                                <ul className="list-disc pl-6 space-y-2">
                                                    <li><strong>Service Providers:</strong> With third-party vendors who perform services on our behalf (e.g., hosting, analytics, payment processing)</li>
                                                    <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                                                    <li><strong>Legal Requirements:</strong> When required by law or to respond to legal process</li>
                                                    <li><strong>Protection:</strong> To protect the rights, property, or safety of {branding?.company_name || 'VoiceBot'}, our users, or the public</li>
                                                    <li><strong>With Your Consent:</strong> When you explicitly authorize us to share specific information</li>
                                    </ul>
                                                <p>
                                                    We require all third parties to respect the security of your personal data and treat it in accordance with applicable laws. They are only permitted to process your data for specified purposes and in accordance with our instructions.
                                    </p>
                                            </div>
                                </section>

                                        <section id="user-rights">
                                            <h2 className="scroll-m-20 text-2xl font-semibold tracking-tight mb-4">6. Your Privacy Rights</h2>
                                            <div className="text-muted-foreground space-y-4 [&>p]:leading-7 [&>ul]:my-4 [&>ul]:ml-6 [&>ul>li]:mt-2">
                                                <p>
                                                    Depending on your location, you may have certain rights regarding your personal information:
                                                </p>
                                                <div className="rounded-lg bg-muted p-4 not-prose space-y-3">
                                                    <div>
                                                        <p className="text-sm font-medium text-foreground mb-1">Right to Access</p>
                                                        <p className="text-sm text-muted-foreground">Request copies of your personal information</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium text-foreground mb-1">Right to Rectification</p>
                                                        <p className="text-sm text-muted-foreground">Request correction of inaccurate or incomplete data</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium text-foreground mb-1">Right to Erasure</p>
                                                        <p className="text-sm text-muted-foreground">Request deletion of your personal information</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium text-foreground mb-1">Right to Restrict Processing</p>
                                                        <p className="text-sm text-muted-foreground">Request limitation on how we use your data</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium text-foreground mb-1">Right to Data Portability</p>
                                                        <p className="text-sm text-muted-foreground">Request transfer of your data to another service</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium text-foreground mb-1">Right to Object</p>
                                                        <p className="text-sm text-muted-foreground">Object to certain types of data processing</p>
                                                    </div>
                                                </div>
                                                <p>
                                                    To exercise these rights, please contact our support team through your account settings. We will respond to your request within the timeframe required by applicable law.
                                                </p>
                                            </div>
                                </section>

                                        <section id="updates">
                                            <h2 className="scroll-m-20 text-2xl font-semibold tracking-tight mb-4">7. Policy Updates</h2>
                                            <div className="text-muted-foreground space-y-4 [&>p]:leading-7 [&>ul]:my-4 [&>ul]:ml-6 [&>ul>li]:mt-2">
                                                <p>
                                                    We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors.
                                                </p>
                                                <p>
                                                    When we make material changes to this policy, we will notify you by:
                                                </p>
                                                <ul className="list-disc pl-6 space-y-2">
                                                    <li>Posting the updated policy on this page</li>
                                                    <li>Updating the "Last updated" date at the top of this policy</li>
                                                    <li>Sending you an email notification (for significant changes)</li>
                                                    <li>Displaying a prominent notice on our platform</li>
                                    </ul>
                                                <p>
                                                    Your continued use of our Services after any changes indicates your acceptance of the updated policy. We encourage you to review this policy periodically to stay informed about how we protect your information.
                                                </p>
                                            </div>
                                        </section>

                                        <section id="contact">
                                            <h2 className="scroll-m-20 text-2xl font-semibold tracking-tight mb-4">8. Contact Us</h2>
                                            <div className="text-muted-foreground space-y-4 [&>p]:leading-7 [&>ul]:my-4 [&>ul]:ml-6 [&>ul>li]:mt-2">
                                                <p>
                                                    If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please don't hesitate to contact us. We are committed to addressing your inquiries promptly and transparently.
                                                </p>
                                                <div className="rounded-lg border bg-card p-4 not-prose shadow-sm">
                                                    <p className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                                                        <MailIcon className="h-4 w-4 text-primary" />
                                                        Data Protection Contact
                                                    </p>
                                                    <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                                                        For privacy-related inquiries, data protection requests, or to exercise your data rights, please contact us through your respective support channels.
                                                    </p>
                                                    <p className="text-xs text-muted-foreground/70">
                                                        We are committed to responding to all data protection requests within 30 days, as required by applicable law.
                                                    </p>
                                                </div>
                                            </div>
                                        </section>
                            </CardContent>
                        </Card>
                            </motion.main>
                </div>

                        {/* Footer */}
                        <motion.footer
                            variants={itemVariants}
                            className="mt-12 pt-8 border-t"
                        >
                            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                <p className="text-xs text-muted-foreground">
                                    {branding?.company_name || 'VoiceBot'} © {new Date().getFullYear()}
                                </p>
                                <nav className="flex items-center gap-4">
                                    <Link 
                                        to="/terms-of-service" 
                                        className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                        Terms of Service
                                    </Link>
                                    <span className="text-muted-foreground">•</span>
                                    <Link 
                                        to="/privacy-policy" 
                                        className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                        Privacy Policy
                                    </Link>
                                </nav>
                            </div>
                        </motion.footer>
            </motion.div>
                </div>
            </div>

            {/* Scroll to Top Button */}
            <AnimatePresence>
                {showScrollTop && (
                    <motion.button
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        onClick={scrollToTop}
                        className="fixed bottom-6 right-6 z-50 flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-colors"
                    >
                        <ArrowUpIcon className="h-4 w-4" />
                    </motion.button>
                )}
            </AnimatePresence>
        </div>
    )
}
